/**
 * Reine Regeln der Projektsperre — „nur eine Person arbeitet gleichzeitig in
 * den Modulen eines Projekts".
 *
 * Bewusst ohne Supabase-Import, ohne Seiteneffekte und ohne eigenen
 * Zeitzugriff: die Zeit kommt immer als Parameter herein. Damit sind die
 * Regeln ohne DOM, ohne Netz und ohne Zeitmanipulation testbar — dieselbe
 * Trennung wie bei projectCheckLogic/projectCheckService (das Projekt hat kein
 * jsdom). Das Einsammeln steht in projectLockService.ts, die Anzeige in der
 * Oberflaeche.
 *
 * Grundhaltung fuer alle Randfaelle: FAIL-OPEN. Ein kaputter oder
 * unverstaendlicher Sperrdatensatz darf niemals jemanden dauerhaft aussperren.
 *
 * ACHTUNG, das hat sich geaendert: Speichern ist seit dem Ausbau der
 * optimistischen Sperre „letzter Schreiber gewinnt" — kein Konflikt-Anker,
 * kein Unsynchron-Dialog, keine Rueckfrage. Die Projektsperre ist damit der
 * EINZIGE Schutz vor Parallelbearbeitung. Fail-open heisst deshalb nicht
 * „harmlos": faellt die Sperre aus, arbeiten zwei Leute gleichzeitig und der
 * zweite Save ueberschreibt den ersten lautlos. Genau darum meldet der Balken
 * den Halter beim Namen, und genau darum laedt die Uebernahme das Projekt neu.
 */

import {
    FOREIGN_BANNER,
    FOREIGN_JUSTIFY_LABEL,
    LOCK_AUFBRECHEN_LABEL,
    LOCK_FREI_GEWORDEN,
    LOCK_KEINE_SPEICHERUNG,
    LOCK_OHNE_HALTER,
    LOCK_UEBERNEHMEN_LABEL,
    lockedProjectWriteMessage,
    queuedUploadBlockedMessage,
} from './readOnlyNotice';

export interface ProjectLock {
    projectId: string;
    userId: string;
    /** Anzeigename des Halters zum Zeitpunkt des Sperrens; null = unbekannt. */
    userLabel: string | null;
    /** Beginn der ununterbrochenen Sperre desselben Nutzers (ISO-8601). */
    acquiredAt: string;
    /** Letztes Lebenszeichen (ISO-8601). */
    heartbeatAt: string;
}

/**
 * Ab wann eine Sperre als verwaist gilt.
 *
 * Muss mit `p_stale_seconds` in supabase/project_locks.sql (180 s)
 * uebereinstimmen: die Datenbank entscheidet ueber die Uebernahme, der Client
 * ueber die Anzeige. Zwei verschiedene Zahlen ergaeben ein Banner, das eine
 * Sperre meldet, die der Server laengst vergeben haette — oder umgekehrt.
 */
export const LOCK_STALE_MS = 180_000;

/**
 * Abstand zwischen zwei Herzschlaegen.
 *
 * Vier Herzschlaege passen in ein Ablauffenster. Drei duerfen ausfallen
 * (Tunnel, Standby, kurzer Netzabriss), ohne dass ein anderer uebernimmt.
 */
export const LOCK_HEARTBEAT_MS = 45_000;

/**
 * Karenzzeit zwischen „raus aus den Projektansichten" und Freigabe.
 *
 * Die Sperre haengt an der ANWESENHEIT im Projekt, nicht am geoeffneten
 * Projekt: wer ueber die Seitenleiste in die Rechnungen, die Kundenakte oder
 * die Projektliste wechselt, arbeitet nicht mehr im Projekt und darf es nicht
 * weiter blockieren (`currentProjectId` bleibt dabei stehen — genau daran hing
 * die Sperre frueher, und ein „geparktes" Projekt sperrte alle Kollegen aus).
 *
 * Ohne Karenzzeit entstuende dabei Flattern: der kurze Sprung „Buchhaltung
 * dieses Projekts oeffnen" und zurueck gaebe die Sperre ab und forderte sie
 * sofort wieder an — zwei RPCs je Klick, dazwischen ein Fenster, in dem ein
 * anderer sie sich holt, und ein Nutzer, der ohne Zutun im Lesemodus landet.
 * 30 s decken jeden dieser Abstecher ab und sind zugleich kurz genug, dass ein
 * geparktes Projekt nicht spuerbar blockiert: freigegeben wird beim ersten
 * Herzschlag nach Ablauf, also spaetestens nach 30 s + LOCK_HEARTBEAT_MS.
 *
 * Waehrend der Karenzzeit wird WEITER geschlagen — wir halten die Sperre ja
 * noch. Ein ausgesetzter Herzschlag hier waere ein zweiter, unsichtbarer
 * Ablaufweg neben LOCK_STALE_MS.
 */
export const LOCK_LEAVE_GRACE_MS = 30_000;

/**
 * ISO-Zeitstempel in Millisekunden — oder null, wenn er unbrauchbar ist.
 *
 * `Date.parse` liefert bei Unsinn NaN und NICHT etwa 0; ohne diese Pruefung
 * rechnete jede Altersbestimmung ab dem 1.1.1970 und meldete jede kaputte
 * Zeile als „frisch gesperrt".
 */
const parseTimestamp = (value: string | null | undefined): number | null => {
    if (!value) return null;
    const ms = Date.parse(value);
    return Number.isFinite(ms) ? ms : null;
};

/** Zwei Zeitpunkte am selben Kalendertag (lokale Zeit des Betrachters)? */
const isSameLocalDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

const pad2 = (value: number): string => String(value).padStart(2, '0');

/**
 * Ist die Sperre abgelaufen (und damit uebernehmbar)?
 *
 * Randfaelle, alle drei in Richtung „offen":
 *   * keine Sperre        → true. Nichts zu halten heisst nichts Frisches.
 *   * kaputter Zeitstempel → true. Eine Zeile, deren Alter sich nicht
 *     bestimmen laesst, darf niemanden aussperren.
 *   * Herzschlag in der ZUKUNFT → NICHT stale. Die Uhr des Betrachters kann
 *     nachgehen; die des Halters ist deshalb nicht falsch. Ein negatives Alter
 *     als „abgelaufen" zu lesen, waere die einzige Richtung, in der eine
 *     Uhrabweichung eine echte, aktive Sperre wegwischt.
 */
export const isLockStale = (
    lock: ProjectLock | null | undefined,
    nowMs: number,
    staleAfterMs: number = LOCK_STALE_MS,
): boolean => {
    if (!lock) return true;
    const heartbeatMs = parseTimestamp(lock.heartbeatAt);
    if (heartbeatMs === null) return true;
    const ageMs = nowMs - heartbeatMs;
    if (ageMs < 0) return false;
    return ageMs > staleAfterMs;
};

/**
 * Haelt jemand ANDERES die Sperre — und lebt sie noch?
 *
 * Nur diese Kombination rechtfertigt das Hinweisbanner. Die eigene Sperre ist
 * keine (man arbeitet in zwei Tabs), eine verwaiste fremde ebenfalls nicht
 * (der Server gaebe sie beim naechsten Aufruf her).
 *
 * Ohne bekannte eigene Kennung (leer/null) gilt jede fremde Sperre als fremd —
 * ein anonymer Betrachter kann nicht behaupten, sie sei seine.
 */
export const isLockHeldByOther = (
    lock: ProjectLock | null | undefined,
    currentUserId: string | null | undefined,
    nowMs: number,
    staleAfterMs: number = LOCK_STALE_MS,
): boolean => {
    if (!lock || !lock.userId) return false;
    if (currentUserId && lock.userId === currentUserId) return false;
    return !isLockStale(lock, nowMs, staleAfterMs);
};

/**
 * Anzeigename des Halters — oder eine ehrliche Umschreibung.
 *
 * Eigene Funktion, weil der Name an zwei Stellen mit unterschiedlichem Satzbau
 * gebraucht wird: im Balken („Wird gerade von X bearbeitet") und in der
 * Rueckfrage vor dem Aufbrechen („Die Bearbeitung liegt derzeit bei X").
 */
export const lockHolderName = (lock: ProjectLock | null | undefined): string => {
    const label = (lock?.userLabel ?? '').trim();
    return label !== '' ? label : 'einem anderen Benutzer';
};

/**
 * Anzeigetext des Sperrbanners.
 *
 * Beispiel: „Wird gerade von Max Mustermann bearbeitet (seit 14:32 Uhr)."
 *
 * Ohne Namen bleibt es bei „von einem anderen Benutzer" — das ist ehrlicher
 * als eine Kennung, mit der niemand etwas anfangen kann. Liegt der Beginn an
 * einem anderen Kalendertag als jetzt, steht das Datum mit dabei; „seit 08:15
 * Uhr" waere sonst irrefuehrend, wenn es der Vortag war. Ohne brauchbaren
 * Beginn entfaellt die Klammer ganz, statt eine erfundene Zeit zu nennen.
 *
 * Ohne Sperre gibt es keinen Text — die Sichtbarkeit entscheidet der Aufrufer
 * ueber `isLockHeldByOther`, nicht diese Funktion ueber einen Platzhaltersatz.
 */
export const describeLockHolder = (
    lock: ProjectLock | null | undefined,
    nowMs: number,
): string => {
    if (!lock) return '';

    const who = lockHolderName(lock);

    const acquiredMs = parseTimestamp(lock.acquiredAt);
    if (acquiredMs === null) return `Wird gerade von ${who} bearbeitet.`;

    const acquired = new Date(acquiredMs);
    const time = `${pad2(acquired.getHours())}:${pad2(acquired.getMinutes())}`;
    const since = isSameLocalDay(acquired, new Date(nowMs))
        ? `seit ${time} Uhr`
        : `seit ${pad2(acquired.getDate())}.${pad2(acquired.getMonth() + 1)}.` +
          `${acquired.getFullYear()}, ${time} Uhr`;

    return `Wird gerade von ${who} bearbeitet (${since}).`;
};

/**
 * ═══ Anwesenheit im Projekt ════════════════════════════════════════════════
 *
 * Was mit der Sperre geschehen soll, entscheidet NICHT das geoeffnete Projekt,
 * sondern die Ansicht: nur in einer Projektansicht (Hub, Module, Archiv —
 * `isProjectView` in navigationPersistence) arbeitet jemand im Projekt.
 *
 * Rein und ohne Zeitzugriff wie alles hier, damit die Verzweigungen ohne
 * jsdom pruefbar sind; die Zustandsfuehrung steht in App.tsx.
 */

/** Was mit der Sperre zu tun ist. */
export type LockPresence =
    /** nichts */
    | 'idle'
    /** wir sind (wieder) im Projekt, halten aber keine Sperre → anfordern */
    | 'acquire'
    /** wir sind drin und halten sie → Herzschlag */
    | 'hold'
    /** wir sind draussen, aber noch in der Karenzzeit → halten und schlagen */
    | 'grace'
    /** wir sind draussen und die Karenzzeit ist um → freigeben */
    | 'release';

export interface LockPresenceInput {
    /** Das geoeffnete Projekt; null = keines. */
    projectId: string | null;
    /** Projekt, dessen Sperre wir tatsaechlich halten (heldLockRef). */
    heldProjectId: string | null;
    /** Steht gerade eine Projektansicht auf dem Schirm? */
    inProjectView: boolean;
    /**
     * Lesemodus — ausdruecklich schreibgeschuetzt, von fremder Sperre ODER
     * fremdes Projekt ohne Begruendung (dort wird ebenfalls nichts angefordert).
     */
    readOnly: boolean;
    /** Wann die Projektansichten verlassen wurden; null = wir sind drin. */
    leftViewAtMs: number | null;
    nowMs: number;
    graceMs?: number;
}

/**
 * Was jetzt mit der Sperre zu tun ist.
 *
 * Drei Regeln, deren Reihenfolge zaehlt:
 *
 *  1. Halten wir eine Sperre an einem ANDEREN Projekt, laeuft gerade ein
 *     Wechsel (handleOpenProject nimmt die neue Sperre, bevor der neue Stand
 *     im State steht). Dann nichts tun — sonst forderte diese Entscheidung
 *     waehrend des Ladens die Sperre des eben verlassenen Projekts neu an.
 *  2. Im Lesemodus wird weder angefordert noch freigegeben. Nicht anfordern
 *     ist der Sinn des Lesemodus; nicht freigeben deshalb, weil die Uebernahme
 *     die Sperre nimmt und den Lesemodus erst nach dem Neuladen ablegt — ein
 *     „release" in diesem Fenster naehme uns die eben geholte Sperre wieder ab.
 *  3. Erst dann entscheidet die Anwesenheit.
 *
 * Ohne bekannten Verlasszeitpunkt gilt Karenz statt Freigabe: eine Sperre
 * wegzunehmen, weil eine Uhrzeit fehlt, waere die falsche Richtung.
 */
export const decideLockPresence = (input: LockPresenceInput): LockPresence => {
    const {
        projectId,
        heldProjectId,
        inProjectView,
        readOnly,
        leftViewAtMs,
        nowMs,
        graceMs = LOCK_LEAVE_GRACE_MS,
    } = input;

    if (heldProjectId !== null && heldProjectId !== projectId) return 'idle';
    if (!projectId) return 'idle';
    if (readOnly) return 'idle';

    const holds = heldProjectId === projectId;
    if (inProjectView) return holds ? 'hold' : 'acquire';
    if (!holds) return 'idle';
    if (leftViewAtMs === null) return 'grace';
    return nowMs - leftViewAtMs >= graceMs ? 'release' : 'grace';
};

/**
 * ═══ Leerlauf: Feierabend im offenen Projekt ═══════════════════════════════
 *
 * Der Anlass ist kein Fehler, sondern Alltag: jemand geht nach Hause und
 * laesst das Projekt offen. Der Rechner laeuft, der Tab lebt, der Herzschlag
 * geht weiter — und damit bleibt die Sperre bis zum naechsten Morgen bestehen.
 * `LOCK_STALE_MS` hilft dagegen nicht: die drei Minuten messen den Ausfall des
 * Herzschlags, nicht die Untaetigkeit des Menschen davor.
 *
 * Gemessen wird deshalb die letzte EINGABE (Klick, Taste, Wischen, Scrollen),
 * nicht die letzte Aenderung am Bericht. Wer eine halbe Stunde lang Plaene
 * ansieht oder ein PDF durchblaettert, arbeitet — er wuerde bei einer
 * „letzte Speicherung"-Messung mitten in der Arbeit hinausgeworfen.
 *
 * Rein und ohne Zeitzugriff wie alles hier; die Zeitgeber und der eigentliche
 * Rueckzug stehen in App.tsx.
 */

/**
 * Wie lange ohne Eingabe, bis Projekt und Sperre von selbst zurueckfallen.
 *
 * 30 Minuten sind lang genug, dass keine Besprechung, kein Telefonat und
 * keine Mittagspause jemanden aus seinem Projekt wirft, und kurz genug, dass
 * ein vergessenes Fenster einen Kollegen hoechstens eine halbe Stunde
 * aufhaelt. Geprueft wird beim Herzschlag, der Rueckzug faellt also
 * spaetestens LOCK_HEARTBEAT_MS nach Ablauf.
 */
export const LOCK_IDLE_EXIT_MS = 30 * 60_000;

/** Dieselbe Frist in vollen Minuten — fuer den Hinweistext. */
export const LOCK_IDLE_EXIT_MINUTES = Math.round(LOCK_IDLE_EXIT_MS / 60_000);

/**
 * Ist der Speicherstand in Sicherheit?
 *
 * WEISSE Liste, und das ist der Punkt: jeder Zustand, den diese Aufzaehlung
 * nicht kennt — 'queued', 'offline', 'storage-error', 'error', 'saving' und
 * jeder kuenftig hinzukommende — gilt als unsicher und verhindert den
 * Rueckzug. Ein automatischer Rauswurf darf nicht ausgerechnet den Stand
 * zuruecklassen, der noch nicht oben ist.
 */
export const isSaveSettled = (status: string | null | undefined): boolean =>
    status === 'idle' || status === 'saved' || status === 'read-only';

export interface IdleExitInput {
    /** Halten wir die Sperre des offenen Projekts? Nur dann gibt es etwas herzugeben. */
    holdsLock: boolean;
    /** Steht eine Projektansicht auf dem Schirm? */
    inProjectView: boolean;
    /** Letzte Eingabe des Nutzers (ms). null = noch keine erfasst. */
    lastActivityAtMs: number | null;
    nowMs: number;
    /** Speicherstatus der Anwendung (SaveStatus, siehe isSaveSettled). */
    saveStatus: string | null | undefined;
    /**
     * Laeuft etwas, das den Rueckzug verbietet? Der mobile Erfassungs-
     * Assistent haelt seine Eingaben nur im Speicher der Komponente; ihn
     * auszuhaengen loeschte genau das, was mobileWizardLock bewahren will.
     */
    held: boolean;
    idleMs?: number;
}

/**
 * Die Grundbedingungen — dieselben fuer Warnung und Rueckzug.
 *
 * Eigene Funktion, weil das Auseinanderlaufen dieser beiden die naheliegende
 * Falle waere: eine Vorwarnung, die zaehlt und zaehlt, waehrend der Rueckzug
 * gar nicht kommen kann (offener Assistent) — oder umgekehrt ein Projekt, das
 * ohne jede Ankuendigung zugeht. Beide fragen deshalb hier.
 *
 * Jeder Zweifel faellt gegen den Rueckzug; er kostet den Nutzer einen
 * Arbeitsplatz, den er sich nicht ausgesucht hat:
 *   * ohne eigene Sperre gibt es nichts freizugeben (Zuschauer bleiben, wo
 *     sie sind).
 *   * ausserhalb der Projektansichten ist niemand „im Projekt"; dort raeumt
 *     die Karenzzeit auf (`decideLockPresence`), nicht diese Regel.
 *   * ohne bekannte letzte Eingabe wird nicht gerechnet.
 */
const idleExitEligible = (input: IdleExitInput): boolean => {
    if (!input.holdsLock || !input.inProjectView) return false;
    if (input.held) return false;
    if (!isSaveSettled(input.saveStatus)) return false;
    return input.lastActivityAtMs !== null;
};

/**
 * Wie lange schon nichts mehr passiert ist.
 *
 * Ein Zeitstempel in der ZUKUNFT (Uhrsprung, Standby, abweichende Uhr) ergibt
 * ein negatives Alter und heisst „gerade eben", nicht „laengst faellig" —
 * deshalb wird bei null geklemmt.
 */
const idleAge = (input: IdleExitInput): number =>
    Math.max(0, input.nowMs - (input.lastActivityAtMs as number));

/** Soll das Projekt jetzt von selbst geschlossen werden? */
export const shouldExitForIdle = (input: IdleExitInput): boolean =>
    idleExitEligible(input) && idleAge(input) >= (input.idleMs ?? LOCK_IDLE_EXIT_MS);

/**
 * ─── Die Vorwarnung ────────────────────────────────────────────────────────
 *
 * Ohne sie hat der Rueckzug eine unschoene Kante: wer aus einer langen
 * Besprechung an den Platz zurueckkommt, findet die Projektliste vor, ohne je
 * die Gelegenheit gehabt zu haben, das zu verhindern.
 *
 * Bewusst KEINE dauerhaft mitlaufende Uhr. Die staende den ganzen Arbeitstag
 * im Bild und zeigte 99 % der Zeit „29 Minuten" — und kostete jede Sekunde
 * ein Neuzeichnen. Sie erscheint erst, wenn es etwas zu entscheiden gibt: wer
 * am Platz sitzt, sieht sie und klickt sie weg; wer nicht da ist, sieht sie
 * nicht, und es passiert genau das, was passieren soll.
 */
export const LOCK_IDLE_WARN_MS = 120_000;

export interface IdleCountdown {
    /** Soll die Vorwarnung stehen? */
    warn: boolean;
    /** Verbleibende Zeit bis zum Rueckzug in ms; 0, sobald sie um ist. */
    remainingMs: number;
    /** Frist ist um — der Aufrufer soll das Projekt jetzt schliessen. */
    expired: boolean;
    /**
     * Wann wieder nachsehen (ms).
     *
     * Der Zeitgeber plant sich damit selbst neu, statt im Sekundentakt zu
     * laufen: ausserhalb der Warnphase schlaeft er bis kurz vor deren Beginn
     * und rechnet beim Aufwachen frisch nach — Eingaben in der Zwischenzeit
     * verschieben den Termin ganz von allein. Gedeckelt, damit die Anzeige
     * nach einem Standby-Sprung nicht minutenlang falsch steht.
     */
    nextCheckMs: number;
}

/** Nichts zu zeigen, in einer Minute nochmal schauen. */
const IDLE_COUNTDOWN_RUHE: IdleCountdown = {
    warn: false,
    remainingMs: 0,
    expired: false,
    nextCheckMs: 30_000,
};

/**
 * Stand der Dinge fuer Anzeige UND Zeitgeber in einem Aufruf.
 *
 * @param warnMs Vorlauf der Warnung vor dem Rueckzug.
 */
export const describeIdleCountdown = (
    input: IdleExitInput,
    warnMs: number = LOCK_IDLE_WARN_MS,
): IdleCountdown => {
    if (!idleExitEligible(input)) return IDLE_COUNTDOWN_RUHE;

    const idleMs = input.idleMs ?? LOCK_IDLE_EXIT_MS;
    const remainingMs = idleMs - idleAge(input);

    // Die Frist ist um. Das Schliessen selbst kann noch scheitern (ein
    // wartender Eintrag der Offline-Warteschlange haelt fest) — deshalb ein
    // knapper naechster Blick statt eines langen Schlafs.
    if (remainingMs <= 0) {
        return { warn: true, remainingMs: 0, expired: true, nextCheckMs: 5_000 };
    }

    // Noch vor der Warnphase: bis kurz vor ihren Beginn schlafen.
    if (remainingMs > warnMs) {
        const bisWarnung = remainingMs - warnMs;
        return {
            warn: false,
            remainingMs,
            expired: false,
            nextCheckMs: Math.min(60_000, Math.max(1_000, bisWarnung)),
        };
    }

    return { warn: true, remainingMs, expired: false, nextCheckMs: 1_000 };
};

/**
 * Restzeit als „m:ss".
 *
 * Aufgerundet, damit die Anzeige nicht bei „0:00" haengt, solange noch eine
 * halbe Sekunde laeuft — und geklemmt, damit sie unter keinen Umstaenden eine
 * negative Zeit zeigt.
 */
export const formatIdleCountdown = (remainingMs: number): string => {
    const total = Math.max(0, Math.ceil((Number.isFinite(remainingMs) ? remainingMs : 0) / 1_000));
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

/**
 * Absage fuer einen SCHREIBENDEN Hintergrundvorgang (Rechnungslauf), wenn ein
 * anderer das Projekt gerade offen hat — sonst null.
 *
 * Seit dem Ausbau der optimistischen Sperre speichert saveProjectToCloud
 * bedingungslos. Ein Rechnungslauf schriebe damit ueber die frisch erfassten
 * Maengel dessen hinweg, der im Pruefmodul sitzt. Die Meldung nennt den Halter
 * beim Namen, weil „bitte spaeter" ohne Adressaten niemandem hilft.
 *
 * FAIL-OPEN: `lock === null` heisst „keine Sperre bekannt" — auch dann, wenn
 * sie nicht gelesen werden konnte (offline, Migration Nr. 33 nicht gefahren).
 * Dieser Fall ergibt null und blockiert nicht.
 */
export const projectWriteBlockedMessage = (
    lock: ProjectLock | null | undefined,
    currentUserId: string | null | undefined,
    nowMs: number,
): string | null =>
    isLockHeldByOther(lock, currentUserId, nowMs)
        ? lockedProjectWriteMessage(lockHolderName(lock))
        : null;

/**
 * ═══ Offline-Queue: darf dieser Eintrag jetzt hoch? ════════════════════════
 *
 * Der gefaehrlichste Schreibweg der App, weil ihn niemand ausloest: die Queue
 * flusht bei `online`, beim Sichtbarwerden des Tabs und alle 45 s von selbst.
 *
 * Das Szenario, das sie ohne Torwaechter kaputtmacht: A arbeitet im Projekt,
 * verliert vier Minuten das Netz. Seine Sperre verfaellt nach drei; B
 * uebernimmt und arbeitet. Kommt As Netz zurueck, schiebt die Queue seinen
 * Stand von vor vier Minuten hoch — und Bs Arbeit ist weg, ohne Meldung, ohne
 * dass A auch nur etwas angeklickt haette.
 */

/**
 * Was mit einem wartenden Queue-Eintrag geschehen soll.
 *
 * Unterschieden wird ueber `reason` und nicht ueber `upload`: das Projekt
 * uebersetzt ohne `strictNullChecks`, und dort taugt ein boolesches Literal
 * nicht als Unterscheidungsmerkmal — der Aufrufer haette den Sperrgrund nur
 * ueber eine Typzusicherung erreicht. Eine Zeichenkette engt in jeder
 * Einstellung sauber ein.
 */
export type QueueUploadDecision =
    /** hochladen */
    | { upload: true; reason: 'ok' }
    /** die eigene Sitzung darf in diesem Projekt gar nicht schreiben */
    | { upload: false; reason: 'read-only' }
    /** ein anderer bearbeitet das Projekt — liegen lassen und melden */
    | { upload: false; reason: 'locked'; holderId: string; message: string }
    /**
     * dieses Bundle ist veraltet (versionGate) — liegen lassen, der NEUE Build
     * laedt nach dem Reload hoch. Am 07.09.2026 hat ein Tab mit altem Build
     * stundenlang alle 45 s frische Zaehler ueberschrieben.
     */
    | { upload: false; reason: 'build-veraltet' }
    /**
     * die Bauphase ist zu (maintenanceLogic) — liegen lassen, KEINE Meldung.
     * Der Server wiese den Save ohnehin mit 'BAUPHASE' ab; der Eintrag bleibt
     * pending und geht nach dem Neuladen am Ende der Bauphase raus.
     */
    | { upload: false; reason: 'bauphase' };

export interface QueueUploadInput {
    /**
     * Ist die AKTUELLE Sitzung fuer dieses Projekt im Lesemodus? Dann wird
     * nichts hochgeladen — auch kein Alteintrag aus der Zeit davor.
     */
    readOnly: boolean;
    /**
     * Die geltende Sperrzeile. `null` heisst „keine Sperre bekannt" — und zwar
     * auch dann, wenn sie NICHT GELESEN WERDEN KONNTE (offline, Migration Nr.
     * 33 nicht gefahren ⇒ PGRST202). Genau dieser Fall muss hochladen duerfen:
     * ein Sperrfehler darf die Arbeit eines Mitarbeiters niemals dauerhaft
     * festhalten. Die Sperre ist organisatorisch, die Queue ist Datenrettung.
     */
    lock: ProjectLock | null | undefined;
    currentUserId: string | null | undefined;
    nowMs: number;
}

/**
 * FAIL-OPEN mit einer einzigen Ausnahme: eine NACHWEISLICH lebende fremde
 * Sperre. Alles andere — keine Zeile, kaputte Zeile, abgelaufene Zeile,
 * unlesbare Zeile, eigene Zeile — laedt hoch.
 */
export const decideQueueUpload = (input: QueueUploadInput): QueueUploadDecision => {
    const { readOnly, lock, currentUserId, nowMs } = input;
    if (readOnly) return { upload: false, reason: 'read-only' };
    if (!isLockHeldByOther(lock, currentUserId, nowMs)) return { upload: true, reason: 'ok' };
    return {
        upload: false,
        reason: 'locked',
        // isLockHeldByOther hat userId bereits geprueft.
        holderId: lock!.userId,
        message: queuedUploadBlockedMessage(lockHolderName(lock)),
    };
};

/**
 * Soll die Absage dem Nutzer GEZEIGT werden?
 *
 * Der Flush laeuft alle 45 s und bei jedem Sichtbarwerden des Tabs. Dieselbe
 * Meldung jedes Mal neu aufpoppen zu lassen, waere Dauerfeuer — nach dem
 * dritten Mal liest sie niemand mehr. Gemerkt wird deshalb, WEM gegenueber wir
 * uns schon entschuldigt haben; wechselt der Halter, ist es eine neue Lage und
 * die Meldung kommt wieder.
 */
export const shouldAnnounceUploadBlock = (
    lastAnnouncedHolderId: string | null | undefined,
    holderId: string,
): boolean => lastAnnouncedHolderId !== holderId;

/**
 * ═══ Sperrverlust beim HALTER ══════════════════════════════════════════════
 *
 * Die Gegenrichtung zum Banner: nicht „darf ich rein", sondern „bin ich
 * rausgeflogen". Ein Admin kann eine lebende Sperre aufbrechen, und ein
 * laengerer Netzausfall laesst sie verfallen. Merkt der bisherige Halter das
 * nicht, tippt er weiter und sein naechster Autosave landet ueber dem Stand,
 * den der Uebernehmende gerade frisch geladen hat — die Uebernahme waere dann
 * ein Versprechen, das die App nicht haelt.
 *
 * Nur eine LEBENDE fremde Sperre gilt als Verlust. Eine geloeschte oder
 * abgelaufene Zeile ist keiner: die holt sich der naechste Herzschlag zurueck,
 * und den Nutzer dafuer in den Lesemodus zu werfen waere ein Aussperren aus
 * dem eigenen Projekt.
 */
export interface LockLossInput {
    /** Projekt, dessen Sperre wir zu halten glauben (heldLockRef). */
    heldProjectId: string | null;
    /** Projekt, ueber dessen Sperrzeile wir gerade etwas erfahren haben. */
    projectId: string;
    lock: ProjectLock | null | undefined;
    currentUserId: string | null | undefined;
    nowMs: number;
}

export const hasLostHeldLock = (input: LockLossInput): boolean =>
    input.heldProjectId === input.projectId &&
    isLockHeldByOther(input.lock, input.currentUserId, input.nowMs);

/**
 * ═══ Entscheidungen der Oberflaeche ════════════════════════════════════════
 *
 * Was im gelben Balken steht und welcher Knopf daneben erscheint, ist reine
 * Regel — kein React. Deshalb steht es hier und nicht in der Komponente: das
 * Projekt hat kein jsdom, Komponenten sind per stehender Entscheidung
 * ungetestet, diese Verzweigungen aber sind genau die, die man testen will.
 */

/** Welche Uebernahme steht dem Betrachter offen? */
export type LockTakeover =
    /** keine — fremde, lebende Sperre und der Betrachter ist kein Admin */
    | 'none'
    /** frei uebernehmbar: die Sperre ist abgelaufen, freigegeben oder unsere */
    | 'acquire'
    /** Aufbrechen einer lebenden fremden Sperre — nur Administratoren */
    | 'force';

/**
 * Admin oder Super-Admin?
 *
 * Das Muster `role === 'admin' || role === 'super_admin'` steht im Projekt an
 * einem Dutzend Stellen inline. Hier bekommt es einen Namen, weil die Frage
 * „darf der die Sperre aufbrechen" getestet werden soll — nicht als Auftrag,
 * die uebrigen Stellen umzubauen.
 */
export const isAdminRole = (role: string | null | undefined): boolean =>
    role === 'admin' || role === 'super_admin';

/**
 * Darf dieser Betrachter die Bearbeitung an sich ziehen — und wie?
 *
 * Die Trennlinie ist bewusst NICHT „Admin ja/nein", sondern der Zustand der
 * Sperre. Eine abgelaufene oder freigegebene Sperre haelt niemanden mehr auf;
 * sie zu nehmen ist der Normalfall (verwaister Rechner, geschlossener Laptop)
 * und braucht keine Sonderrechte — die Datenbank vergibt sie ohnehin an den
 * naechsten Anfrager. Nur das Wegnehmen einer LEBENDEN fremden Sperre ist ein
 * Eingriff und bleibt Administratoren vorbehalten; dort haengt echte,
 * ungespeicherte Arbeit eines anderen dran.
 */
export const canTakeOverLock = (
    lock: ProjectLock | null | undefined,
    currentUserId: string | null | undefined,
    role: string | null | undefined,
    nowMs: number,
    staleAfterMs: number = LOCK_STALE_MS,
): LockTakeover => {
    if (!isLockHeldByOther(lock, currentUserId, nowMs, staleAfterMs)) return 'acquire';
    return isAdminRole(role) ? 'force' : 'none';
};

/** Warum steht der Balken da? */
export type ProjectNoticeKind =
    /** gar nicht */
    | 'none'
    /** schreibgeschuetzt geoeffnet (z. B. aus „Offene Positionen") */
    | 'read-only'
    /**
     * fremdes Projekt, noch ohne Begruendung fuer heute (SQL Nr. 128) —
     * der Balken bietet den Begruendungsdialog an, keine Uebernahme
     */
    | 'foreign'
    /** jemand anderes hat die Projektsperre */
    | 'lock';

export interface ProjectNotice {
    kind: ProjectNoticeKind;
    text: string;
    takeover: LockTakeover;
    /** Aufschrift des Knopfes; leer, wenn keiner erscheint. */
    takeoverLabel: string;
    /**
     * Aufschrift des Begruendungsknopfes — nur bei kind 'foreign' gesetzt.
     *
     * Optional statt Pflicht, weil ProjectNotice auch als Literal gebaut wird
     * (Tests, mobileWizardLock): dort gibt es keinen Begruendungsweg, und ein
     * Pflichtfeld erzwaenge ueberall ein leeres ''.
     */
    justifyLabel?: string;
}

export const NO_PROJECT_NOTICE: ProjectNotice = {
    kind: 'none',
    text: '',
    takeover: 'none',
    takeoverLabel: '',
};

/** Balken ohne Sperrbezug — nur der Nur-Ansicht-Grund, kein Knopf. */
export const readOnlyProjectNotice = (text: string): ProjectNotice => ({
    kind: 'read-only',
    text,
    takeover: 'none',
    takeoverLabel: '',
});

/**
 * Balken fuer ein fremdes Projekt ohne Begruendung von heute.
 *
 * `takeover` bleibt 'none': der Weg ins Bearbeiten fuehrt hier ueber die
 * Begruendung, nicht ueber die Sperre. Derselbe Wortlaut in Hub und Pruefmodul
 * — der Grund ist in beiden derselbe.
 */
export const FOREIGN_PROJECT_NOTICE: ProjectNotice = {
    kind: 'foreign',
    text: FOREIGN_BANNER,
    takeover: 'none',
    takeoverLabel: '',
    justifyLabel: FOREIGN_JUSTIFY_LABEL,
};

export interface ProjectNoticeInput {
    /**
     * Nur-Ansicht aus einem ANDEREN Grund als der Sperre — die Ansicht wurde
     * bewusst schreibgeschuetzt geoeffnet (AdminOpenPositionsView).
     */
    explicitReadOnly: boolean;
    /**
     * Fremdes Projekt ohne gueltige Begruendung von heute: geoeffnet zum
     * Ansehen, die Sperre wurde gar nicht erst angefordert (App.handleOpenProject).
     */
    foreignPending: boolean;
    /** Die Sperre gehoerte beim Oeffnen jemand anderem; wir schauen nur zu. */
    lockBlocked: boolean;
    lock: ProjectLock | null | undefined;
    currentUserId: string | null | undefined;
    role: string | null | undefined;
    nowMs: number;
    /** Wortlaut fuer den Nur-Ansicht-Grund; je Ansicht verschieden. */
    readOnlyText: string;
}

/**
 * Der komplette Balken in einem Schritt.
 *
 * Reihenfolge der Zweige ist Absicht: der ausdrueckliche Schreibschutz gewinnt
 * gegen den Sperrhinweis. Wer ein Projekt aus „Offene Positionen" nur ansehen
 * wollte, soll dort keinen Uebernahme-Knopf bekommen — das waere ein
 * Schreibrecht, das dieser Weg nie vergeben hat. (Dieser Weg fordert die
 * Sperre gar nicht erst an, siehe App.handleOpenProject.)
 *
 * Das fremde Projekt steht ZWISCHEN beiden: hinter dem ausdruecklichen
 * Schreibschutz (der vergibt nie ein Schreibrecht, auch nicht per
 * Begruendung), aber vor der Sperre. Solange die Begruendung fehlt, wurde die
 * Sperre gar nicht angefordert — ein Sperrhinweis waere dort eine Auskunft
 * ueber einen Vorgang, der nicht stattgefunden hat, und sein Knopf ein Weg an
 * der Begruendung vorbei. Die Sperre kommt erst nach der Begruendung ins Spiel;
 * haelt sie dann ein anderer, gilt wieder der Sperrzweig.
 *
 * Der letzte Zustand ist der interessante: `lockBlocked`, aber die Sperre lebt
 * nicht mehr. Das passiert im offenen Fenster — der andere hat geschlossen
 * oder sein Herzschlag ist ausgeblieben. Der Balken bleibt dann stehen (wir
 * sind weiterhin im Lesemodus und haben einen alten Stand im Speicher), wechselt
 * aber Text und Knopf: aus „bitte warten" wird „uebernehmen".
 */
export const describeProjectNotice = (input: ProjectNoticeInput): ProjectNotice => {
    const { explicitReadOnly, foreignPending, lockBlocked, lock, currentUserId, role, nowMs, readOnlyText } = input;

    if (explicitReadOnly) return readOnlyProjectNotice(readOnlyText);
    if (foreignPending) return FOREIGN_PROJECT_NOTICE;
    if (!lockBlocked) return NO_PROJECT_NOTICE;

    const takeover = canTakeOverLock(lock, currentUserId, role, nowMs);

    if (takeover === 'acquire') {
        return {
            kind: 'lock',
            text: LOCK_FREI_GEWORDEN,
            takeover,
            takeoverLabel: LOCK_UEBERNEHMEN_LABEL,
        };
    }

    const holder = describeLockHolder(lock, nowMs);
    return {
        kind: 'lock',
        text: `${holder !== '' ? holder : LOCK_OHNE_HALTER} ${LOCK_KEINE_SPEICHERUNG}`,
        takeover,
        takeoverLabel: takeover === 'force' ? LOCK_AUFBRECHEN_LABEL : '',
    };
};

/**
 * ═══ Sperrhinweis in LISTEN ════════════════════════════════════════════════
 *
 * Die Gegenrichtung zum Balken: der zeigt die Sperre, wenn man schon drin ist.
 * Das hilft niemandem, der vor der Projektliste sitzt — der klickt hinein und
 * landet ueberrascht im Lesemodus. Deshalb steht der Hinweis jetzt schon an
 * der Zeile bzw. Kachel.
 *
 * Es bleibt eine WARNUNG, kein Riegel: ein gesperrtes Projekt laesst sich
 * weiterhin oeffnen (dann eben nur zum Ansehen). Die Liste entscheidet nichts
 * anders als heute, sie sagt nur vorher Bescheid.
 */

/**
 * Wie viele Projekte eine Liste hoechstens nach ihrer Sperre fragt.
 *
 * Die Projektliste kann im Archivfilter mehrere hundert Zeilen zeigen. Der
 * Hinweis ist Beiwerk; er darf die Liste weder verzoegern noch die Datenbank
 * mit einem `in`-Filter ueber tausende Kennungen belasten. Wer so weit
 * herunterscrollt, sieht dort schlicht keinen Sperrhinweis — das ist der
 * richtige Ausfall fuer eine Zusatzinformation.
 */
export const LOCK_LIST_MAX_PROJECTS = 300;

/**
 * Groesse eines einzelnen `in`-Filters.
 *
 * PostgREST reicht den Filter als Teil der URL; sehr lange `in`-Listen laufen
 * dort in Laengenbegrenzungen von Proxys. Hundert Kennungen sind rund 3,7 kB
 * und damit weit unter jeder ueblichen Grenze.
 */
export const LOCK_LIST_CHUNK_SIZE = 100;

/**
 * Die Kennungen, nach denen wirklich gefragt wird: ohne Leereintraege, ohne
 * Doppelte, gedeckelt.
 *
 * Doppelte kommen tatsaechlich vor — die Uebersicht zeigt dasselbe Projekt in
 * der Kalenderwoche und in „nicht eingeplant", und ein Vertretungsprojekt
 * taucht in zwei Gruppen auf. Ungefiltert stuende dieselbe Kennung mehrfach im
 * `in`-Filter.
 */
export const pickLockQueryIds = (
    projectIds: ReadonlyArray<string | null | undefined>,
    max: number = LOCK_LIST_MAX_PROJECTS,
): string[] => {
    if (max <= 0) return [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of projectIds || []) {
        if (typeof raw !== 'string') continue;
        const id = raw.trim();
        if (id === '' || seen.has(id)) continue;
        seen.add(id);
        out.push(id);
        if (out.length >= max) break;
    }
    return out;
};

/** Zerlegt die Kennungen in Haeppchen fuer je einen `in`-Filter. */
export const chunkLockQueryIds = (
    ids: ReadonlyArray<string>,
    size: number = LOCK_LIST_CHUNK_SIZE,
): string[][] => {
    const step = size > 0 ? size : LOCK_LIST_CHUNK_SIZE;
    const out: string[][] = [];
    for (let i = 0; i < (ids?.length || 0); i += step) out.push(ids.slice(i, i + step));
    return out;
};

/** Wessen Sperre ist das aus Sicht des Betrachters? */
export type ProjectLockBadgeKind =
    /** unsere eigene — neutral melden, nicht als Hindernis */
    | 'self'
    /** die eines anderen — das ist die eigentliche Warnung */
    | 'other';

export interface ProjectLockBadge {
    kind: ProjectLockBadgeKind;
    /** Kurztext an der Zeile/Kachel. */
    label: string;
    /** Langtext fuer `title`/`aria-label` — derselbe Wortlaut wie im Balken. */
    title: string;
}

/** Neutraler Text fuer die eigene Sperre. */
export const LOCK_BADGE_SELF = 'Von Ihnen geöffnet';

/** Fremde Sperre ohne brauchbaren Namen. */
export const LOCK_BADGE_OTHER_ANONYMOUS = 'Wird gerade bearbeitet';

/**
 * Der Sperrhinweis einer Listenzeile — oder null, wenn keiner hingehoert.
 *
 * `null` in drei Faellen, und alle drei sind Absicht:
 *   * keine Sperrzeile (auch: konnte nicht gelesen werden, Migration Nr. 33
 *     nicht gefahren, offline). Die Liste sieht dann aus wie immer.
 *   * abgelaufene Sperre. Die haelt niemanden auf; sie anzuzeigen hiesse, ein
 *     Projekt als belegt zu melden, das der naechste Klick problemlos oeffnet.
 *
 * Die EIGENE Sperre ist bewusst KEINE Warnung. Sie kommt im Alltag staendig
 * vor — zweiter Tab, Rueckweg aus den Rechnungen, gerade erst geschlossen —
 * und ein rotes „belegt" am eigenen Projekt wuerde nur verunsichern. Sie
 * bekommt darum eine eigene, neutrale Kennzeichnung statt gar keiner: dass die
 * Sperre existiert, erklaert dem Nutzer, warum ein Kollege gerade nicht
 * hineinkommt.
 *
 * Der Kurztext nennt den Halter ohne Uhrzeit (dafuer ist an einer Kachel kein
 * Platz), der Langtext ist woertlich der des Balkens — damit niemand denselben
 * Sachverhalt an zwei Stellen unterschiedlich formuliert vorfindet.
 */
export const describeProjectLockBadge = (
    lock: ProjectLock | null | undefined,
    currentUserId: string | null | undefined,
    nowMs: number,
    staleAfterMs: number = LOCK_STALE_MS,
): ProjectLockBadge | null => {
    if (!lock || !lock.userId) return null;
    if (isLockStale(lock, nowMs, staleAfterMs)) return null;

    if (currentUserId && lock.userId === currentUserId) {
        return { kind: 'self', label: LOCK_BADGE_SELF, title: LOCK_BADGE_SELF + '.' };
    }

    const name = (lock.userLabel ?? '').trim();
    return {
        kind: 'other',
        label: name !== '' ? `${name} bearbeitet gerade` : LOCK_BADGE_OTHER_ANONYMOUS,
        title: describeLockHolder(lock, nowMs),
    };
};
